import { z, type ZodType } from 'zod';

import { appConfig } from '@/config/app.config';
import { HTTP_STATUS } from '@/constants/http.constants';
import { sessionTokenStore } from '@/services/session/token-store';
import type { ApiResponse, Page, PaginationMeta } from '@/types/api.types';
import { ERROR_CODES } from '@/types/api.types';
import { fileNameFromDisposition } from '@/utils/file';

import { ApiError } from './api-error';

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Readonly<Record<string, QueryValue>>;

export interface HttpClientOptions {
  baseUrl: string;
  timeoutMs: number;
  /** Called before every request — where the bearer token comes from. */
  getHeaders?: () => Record<string, string>;
  /** Called when the API answers 401: the session is gone, so the app signs out. */
  onUnauthorized?: () => void;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Sent as JSON, except a `Blob` or `File`, which goes up as raw bytes under its own type. */
  body?: unknown;
  /** Appended as a query string; `undefined`, `null` and `''` values are left out. */
  query?: QueryParams | undefined;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Validates the unwrapped `data` — the last boundary where untyped JSON becomes typed. */
  schema?: ZodType<T>;
}

export interface PageOptions<T> {
  query?: QueryParams | undefined;
  signal?: AbortSignal | undefined;
  /** Schema of ONE item; the client validates the whole array with it. */
  schema: ZodType<T>;
}

export interface HttpClient {
  request<T>(path: string, options?: RequestOptions<T>): Promise<T>;
  get<T>(path: string, options?: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T>;
  /** A paginated list endpoint: returns the items *and* the envelope's pagination `meta`. */
  getPage<T>(path: string, options: PageOptions<T>): Promise<Page<T>>;
  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, 'method'>): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, 'method'>): Promise<T>;
  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions<T>, 'method'>): Promise<T>;
  delete<T>(path: string, options?: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T>;
  /**
   * A file the API streams instead of a JSON envelope (e.g. a PDF). Signed and timed out like
   * every other request; a failure still arrives as an ApiError from the JSON error envelope.
   */
  getBlob(
    path: string,
    options?: { signal?: AbortSignal | undefined },
  ): Promise<{ blob: Blob; fileName: string | null }>;
}

const paginationMetaSchema: ZodType<PaginationMeta> = z.object({
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

function isApiResponse(value: unknown): value is ApiResponse<unknown> {
  return typeof value === 'object' && value !== null && 'success' in value;
}

export function buildQueryString(query: QueryParams | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Turns the backend's error envelope into an ApiError; falls back for non-envelope responses. */
function toApiError(status: number, payload: unknown): ApiError {
  if (isApiResponse(payload) && !payload.success) {
    const { error } = payload;
    return new ApiError({
      status,
      code: error.code,
      message: error.message,
      ...(error.details ? { details: error.details } : {}),
      requestId: error.requestId,
    });
  }

  return new ApiError({
    status,
    code: ERROR_CODES.INTERNAL_ERROR,
    message: `Request failed with status ${status}`,
  });
}

function parse<T>(schema: ZodType<T>, value: unknown, status: number): T {
  const parsed = schema.safeParse(value);
  if (parsed.success) return parsed.data;
  throw new ApiError({
    status,
    code: ERROR_CODES.INTERNAL_ERROR,
    message: 'The server returned data in an unexpected shape',
    cause: parsed.error,
  });
}

interface Envelope {
  status: number;
  data: unknown;
  meta: unknown;
}

export function createHttpClient({
  baseUrl,
  timeoutMs,
  getHeaders,
  onUnauthorized,
}: HttpClientOptions): HttpClient {
  /** Sends the request and returns the raw response, or throws an ApiError if it never arrived. */
  async function open(
    path: string,
    options: RequestOptions<unknown>,
    accept = 'application/json',
  ): Promise<Response> {
    const { method = 'GET', body, query, headers = {}, signal } = options;

    const requestHeaders: Record<string, string> = {
      Accept: accept,
      ...getHeaders?.(),
      ...headers,
    };
    // A file (e.g. a member's photo) is the request body itself; everything else is JSON.
    const isBinary = body instanceof Blob;
    if (body !== undefined) {
      requestHeaders['Content-Type'] = isBinary
        ? body.type || 'application/octet-stream'
        : 'application/json';
    }

    // The caller's own abort signal still wins; the timeout is an additional one.
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}${buildQueryString(query)}`, {
        method,
        headers: requestHeaders,
        ...(body === undefined ? {} : { body: isBinary ? body : JSON.stringify(body) }),
        signal: combinedSignal,
      });
    } catch (error) {
      if (timeoutSignal.aborted) throw ApiError.timeout(timeoutMs, error);
      // A caller-initiated abort is not an error condition — let it propagate.
      if (signal?.aborted) throw error;
      throw ApiError.network('Could not reach the server', error);
    }

    if (response.status === HTTP_STATUS.UNAUTHORIZED) onUnauthorized?.();
    return response;
  }

  /** Sends the request and returns the success envelope's parts, or throws an ApiError. */
  async function send(path: string, options: RequestOptions<unknown>): Promise<Envelope> {
    const response = await open(path, options);
    if (response.status === 204) return { status: 204, data: undefined, meta: undefined };

    let payload: unknown;
    try {
      payload = await response.json();
    } catch (error) {
      if (response.ok) throw ApiError.network('The server returned a malformed response', error);
      throw toApiError(response.status, undefined);
    }

    if (!response.ok) throw toApiError(response.status, payload);
    if (!isApiResponse(payload) || !payload.success) throw toApiError(response.status, payload);

    return { status: response.status, data: payload.data, meta: payload.meta };
  }

  async function request<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
    const { schema, ...rest } = options;
    const envelope = await send(path, rest);
    if (!schema) return envelope.data as T;
    return parse(schema, envelope.data, envelope.status);
  }

  async function getPage<T>(path: string, { schema, query, signal }: PageOptions<T>) {
    const envelope = await send(path, { query, ...(signal ? { signal } : {}) });
    return {
      items: parse(z.array(schema), envelope.data, envelope.status),
      meta: parse(paginationMetaSchema, envelope.meta, envelope.status),
    };
  }

  async function getBlob(path: string, { signal }: { signal?: AbortSignal | undefined } = {}) {
    const response = await open(path, signal ? { signal } : {}, '*/*');
    if (response.ok) {
      return {
        blob: await response.blob(),
        fileName: fileNameFromDisposition(response.headers.get('Content-Disposition')),
      };
    }
    // Errors still come back as the JSON envelope.
    const payload: unknown = await response.json().catch(() => undefined);
    throw toApiError(response.status, payload);
  }

  return {
    request,
    getPage,
    getBlob,
    get: (path, options) => request(path, { ...options, method: 'GET' }),
    post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
    patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
    put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
    delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  };
}

/** The shared client every feature's api layer uses. It signs requests with the session token. */
export const httpClient = createHttpClient({
  baseUrl: appConfig.api.baseUrl,
  timeoutMs: appConfig.api.timeoutMs,
  getHeaders: (): Record<string, string> => {
    const token = sessionTokenStore.get();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  onUnauthorized: () => {
    sessionTokenStore.clear();
  },
});
